'use client';

import '@ant-design/v5-patch-for-react-19';
import 'antd/dist/reset.css';

import React, {useEffect, useState} from "react";
import {AntdRegistry} from "@ant-design/nextjs-registry";
import ThemeSwitcher from "@/components/ui/float-buttons/theme-switcher";
import {ConfigProvider, FloatButton, theme} from "antd";
import {usePathname} from "next/navigation";
import AppThemeContext from "@/contexts/AppThemeContext";

const THEME_COOKIE_NAME = 'theme';
const DARK = 'dark';
const LIGHT = 'light';

function AppWrapper({children}: React.PropsWithChildren) {
    const {defaultAlgorithm, darkAlgorithm} = theme;
    const [isDarkMode, setIsDarkMode] = useState(false);
    const pathname = usePathname();

    const getCookieTheme = (): string | null => {
        const match = document.cookie.match(new RegExp('(^| )' + THEME_COOKIE_NAME + '=([^;]+)'));
        return match ? decodeURIComponent(match[2]) : null;
    };

    const setCookieTheme = (value: string) => {
        document.cookie = `${THEME_COOKIE_NAME}=${value}; path=/; max-age=31536000`;
    };

    useEffect(() => {
        const cookieTheme = getCookieTheme();

        if (cookieTheme === DARK || cookieTheme === LIGHT) {
            setIsDarkMode(cookieTheme === DARK);
        } else {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            setIsDarkMode(mediaQuery.matches);

            const handler = (event: MediaQueryListEvent) => {
                setIsDarkMode(event.matches);
            };
            mediaQuery.addEventListener('change', handler);

            return () => mediaQuery.removeEventListener('change', handler);
        }
    }, []);

    const handleThemeChange = () => {
        setIsDarkMode((prev) => {
            const newValue = !prev;
            setCookieTheme(newValue ? DARK : LIGHT);
            return newValue;
        });
    };

    const themeConfig = {
        algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
        cssVar: true,
        token: {
            wireframe: false,
            colorPrimary: '#00a380',
            colorInfo: '#00a380',
            customRechartsTooltipBackground: isDarkMode ? 'rgba(31, 31, 31, 0.9)' : 'rgba(255, 255, 255, 0.9)',
            customYandexMapsFilter: isDarkMode ? 'invert(90%) hue-rotate(180deg) brightness(0.8) contrast(1)' : '',
        }
    };

    const shouldHideThemeSwitcher = /^\/rules-of-conduct\/[^/]+\/details\/?$/.test(pathname);

    return (
        <AppThemeContext.Provider value={isDarkMode}>
            <ConfigProvider
                theme={themeConfig}>
                <AntdRegistry>
                    {!shouldHideThemeSwitcher && (
                        <FloatButton.Group shape="circle" style={{insetInlineEnd: 24}}>
                            <ThemeSwitcher isDarkMode={isDarkMode} onThemeToggle={handleThemeChange}/>
                        </FloatButton.Group>
                    )}
                    {children}
                </AntdRegistry>
            </ConfigProvider>
        </AppThemeContext.Provider>
    );
};

export default AppWrapper;
